import type React from 'react';

import { buildQueryParams } from '../../../../common/misc';
import { API_URL } from '../../../../config/config';
import type { CardDataType } from '../../../types/Card';

interface MarqetaURIParams {
    cardId: string;
    cardDataType: CardDataType;
}
export const buildMarqetaCardDataUrl = ({
    cardId,
    cardDataType,
}: MarqetaURIParams) =>
    `${API_URL}/card-details${buildQueryParams({ cardId, cardDataType })}`;

export interface Script {
    src?: string;
    content?: string;
}

interface InjectProps {
    html: string;
    containerRef: React.RefObject<HTMLDivElement | null>;
    error: string | null;
}

/**
 * Injects Marqeta scripts into the DOM in a staggered manner to prevent runtime errors.
 *
 * This function loads the Marqeta scripts in a specific order:
 * 1. It first injects the initialization script (marqetaInitScript).
 * 2. Once the initialization script is loaded, it then injects the bootstrap script (marqetaBootstrapScript).
 *
 * This staggered loading is necessary because the bootstrap script depends on
 * functionality provided by the initialization script. Loading them simultaneously
 * or in the wrong order could lead to runtime errors.
 *
 * @param html - The HTML string containing the scripts to inject
 * @param containerRef - A reference to the container element where the scripts will be injected
 * @param error - An error object, if any error occurred during the process
 */
export function injectMarqetaScripts({
    html,
    containerRef,
    error,
}: InjectProps) {
    const { content, scripts } = extractContentAndScripts(html);
    if (containerRef.current != null) {
        containerRef.current.innerHTML = content;
        if (error == null) {
            const marqetaInitScript = document.createElement('script');
            const marqetaBootstrapScript = document.createElement('script');
            marqetaInitScript.src = scripts[0]?.src ?? '';
            marqetaInitScript.type = 'text/javascript';
            marqetaInitScript.addEventListener('load', () => {
                marqetaBootstrapScript.textContent = scripts[1]?.content ?? '';
                marqetaBootstrapScript.type = 'text/javascript';
                containerRef.current?.appendChild(marqetaBootstrapScript);
            });

            containerRef.current.appendChild(marqetaInitScript);
        }
    }
}
/**
 * Extracts content and scripts from an HTML string.
 *
 * This function is necessary because browsers prevent cross-origin script execution for security reasons.
 * By parsing the HTML and extracting the scripts, we can then inject them manually,
 * allowing us to execute scripts from a different origin in a controlled manner.
 *
 * @param htmlString - The HTML string to parse
 * @returns An object containing the HTML content and an array of extracted scripts
 */
function extractContentAndScripts(htmlString: string): {
    content: string;
    scripts: Script[];
} {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');

    const scripts: Script[] = [];

    doc.querySelectorAll('script').forEach((scriptElement) => {
        if (scriptElement.src) {
            scripts.push({ src: scriptElement.src });
        } else {
            scripts.push({ content: scriptElement.textContent ?? '' });
        }
        scriptElement.remove();
    });

    return {
        content: doc.body.innerHTML,
        scripts,
    };
}
